'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import { toast, Toaster } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import Breadcrumb from '@/components/Breadcrumb';
import ConfirmDialog from '@/components/ui/ConfirmDialog';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Manutenzione {
  id: string;
  titolo: string;
  tipo: string;
  reparto: string;
  assegnato_a_nome: string;
  data_pianificata: string;
  data_esecuzione: string | null;
  durata_ore: number;
  costo: number;
  note: string;
  completata: boolean;
  created_at: string;
}

interface ChangeRequest {
  id: string;
  numero_change: string;
  titolo: string;
  descrizione: string;
  stato: string;
  priorita: string;
  reparto: string;
  proposto_da_nome: string;
  approvato_da_nome: string;
  data_pianificata: string | null;
  impatto: string;
  piano_rollback: string;
  created_at: string;
}

const changeStatoColor: Record<string, string> = {
  proposta: 'bg-blue-500/20 text-blue-400',
  in_approvazione: 'bg-amber-500/20 text-amber-400',
  approvata: 'bg-green-500/20 text-green-400',
  pianificata: 'bg-purple-500/20 text-purple-400',
  in_esecuzione: 'bg-orange-500/20 text-orange-400',
  verificata: 'bg-teal-500/20 text-teal-400',
  chiusa: 'bg-slate-500/20 text-slate-400',
  rifiutata: 'bg-red-500/20 text-red-400',
};

export default function ManutenzioneContent() {
  const [tab, setTab] = useState<'manutenzioni' | 'change'>('manutenzioni');
  const [manutenzioni, setManutenzioni] = useState<Manutenzione[]>([]);
  const [changes, setChanges] = useState<ChangeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingManutenzione, setEditingManutenzione] = useState<Manutenzione | null>(null);
  const [formMan, setFormMan] = useState({ titolo: '', tipo: 'preventiva', reparto: '', assegnato_a_nome: '', data_pianificata: '', note: '' });
  const [formChange, setFormChange] = useState({ titolo: '', descrizione: '', priorita: 'normale', reparto: '', proposto_da_nome: '', impatto: '', piano_rollback: '' });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string; tipo: 'manutenzione' | 'change' }>({ open: false, id: '', name: '', tipo: 'manutenzione' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [{ data: m }, { data: c }] = await Promise.all([
      supabase.from('manutenzioni').select('*').order('data_pianificata'),
      supabase.from('change_request').select('*').order('created_at', { ascending: false }),
    ]);
    setManutenzioni(m || []);
    setChanges(c || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const completaManutenzione = async (id: string) => {
    await supabase.from('manutenzioni').update({ completata: true, data_esecuzione: new Date().toISOString() }).eq('id', id);
    fetchData();
  };

  const eliminaItem = async () => {
    const { id, tipo } = deleteConfirm;
    try {
      const table = tipo === 'manutenzione' ? 'manutenzioni' : 'change_request';
      const { error } = await supabase.from(table).delete().eq('id', id);
      if (error) throw error;
      toast.success(tipo === 'manutenzione' ? 'Manutenzione eliminata' : 'Change request eliminata');
      setDeleteConfirm({ open: false, id: '', name: '', tipo: 'manutenzione' });
      fetchData();
    } catch (err: any) {
      toast.error('Errore eliminazione: ' + err.message);
    }
  };

  const openEditManutenzione = (m: Manutenzione) => {
    setEditingManutenzione(m);
    setFormMan({
      titolo: m.titolo,
      tipo: m.tipo,
      reparto: m.reparto || '',
      assegnato_a_nome: m.assegnato_a_nome || '',
      data_pianificata: m.data_pianificata ? m.data_pianificata.slice(0, 10) : '',
      note: m.note || '',
    });
    setShowForm(true);
  };

  const creaChange = async () => {
    if (!formChange.titolo.trim()) { toast.error('Titolo obbligatorio'); return; }
    if (!formChange.descrizione.trim()) { toast.error('Descrizione obbligatoria'); return; }
    setSaving(true);
    const numero = `CR-${Date.now()}-${Math.floor(Math.random() * 100).toString().padStart(2, '0')}`;
    const { error } = await supabase.from('change_request').insert({ ...formChange, numero_change: numero });
    if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Change request creata'); }
    setShowForm(false);
    setFormChange({ titolo: '', descrizione: '', priorita: 'normale', reparto: '', proposto_da_nome: '', impatto: '', piano_rollback: '' });
    fetchData();
    setSaving(false);
  };

  const creaManutenzione = async () => {
    if (!formMan.titolo.trim()) { toast.error('Titolo obbligatorio'); return; }
    if (!formMan.data_pianificata) { toast.error('Data pianificata obbligatoria'); return; }
    setSaving(true);
    if (editingManutenzione) {
      const { error } = await supabase.from('manutenzioni').update(formMan).eq('id', editingManutenzione.id);
      if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Manutenzione aggiornata'); }
    } else {
      const { error } = await supabase.from('manutenzioni').insert(formMan);
      if (error) { toast.error('Errore: ' + error.message); } else { toast.success('Manutenzione creata'); }
    }
    setShowForm(false);
    setEditingManutenzione(null);
    setFormMan({ titolo: '', tipo: 'preventiva', reparto: '', assegnato_a_nome: '', data_pianificata: '', note: '' });
    fetchData();
    setSaving(false);
  };

  const approvaChange = async (id: string, nuovoStato: string) => {
    await supabase.from('change_request').update({ stato: nuovoStato }).eq('id', id);
    fetchData();
  };

  return (
    <div className="min-h-full bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />

      <ConfirmDialog
        open={deleteConfirm.open}
        title={deleteConfirm.tipo === 'manutenzione' ? 'Elimina Manutenzione' : 'Elimina Change Request'}
        message={`Sei sicuro di voler eliminare "${deleteConfirm.name}"? Questa azione non può essere annullata.`}
        confirmLabel="Elimina"
        onConfirm={eliminaItem}
        onCancel={() => setDeleteConfirm({ open: false, id: '', name: '', tipo: 'manutenzione' })}
        danger
      />

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-surface-elevated rounded-xl p-1 w-fit">
        {(['manutenzioni', 'change'] as const).map(t => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${tab === t ? 'bg-sky-600 text-white' : 'text-muted-color hover:text-white'}`}>
            {t === 'manutenzioni' ? 'Manutenzioni Preventive' : 'Change Request'}
          </button>
        ))}
      </div>

      <Breadcrumb items={[{ label: 'Manutenzione e Change' }]} />

      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-muted-color">
          {tab === 'manutenzioni' ? `${manutenzioni.length} manutenzioni pianificate` : `${changes.length} change request`}
        </p>
        <button onClick={() => setShowForm(true)} className="flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors">
          <Icon name="PlusIcon" size={16} /> {tab === 'manutenzioni' ? 'Nuova Manutenzione' : 'Nuova Change'}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12"><div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-3">
          {tab === 'manutenzioni' ? (
            manutenzioni.length === 0 ? <div className="text-center py-12 text-muted-color">Nessuna manutenzione pianificata</div> :
            manutenzioni.map(m => (
              <div key={m.id} className="bg-surface-elevated border border-border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${m.completata ? 'bg-green-500/20 text-green-400' : 'bg-amber-500/20 text-amber-400'}`}>
                        {m.completata ? 'Completata' : 'Pianificata'}
                      </span>
                      <span className="text-xs bg-surface text-muted-color px-2 py-0.5 rounded-full">{m.tipo}</span>
                    </div>
                    <p className="text-sm font-semibold text-white">{m.titolo}</p>
                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-color">
                      {m.reparto && <span>📍 {m.reparto}</span>}
                      {m.assegnato_a_nome && <span>👤 {m.assegnato_a_nome}</span>}
                      <span>📅 {new Date(m.data_pianificata).toLocaleDateString('it-IT')}</span>
                      {m.costo > 0 && <span>💶 €{m.costo}</span>}
                    </div>
                    {m.note && <p className="text-xs text-muted-color mt-1">{m.note}</p>}
                  </div>
                  <div className="flex items-center gap-2">
                    {!m.completata && (
                      <button onClick={() => completaManutenzione(m.id)} className="flex items-center gap-1 bg-green-600/20 hover:bg-green-600/40 text-green-400 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap">
                        <Icon name="CheckIcon" size={14} /> Completa
                      </button>
                    )}
                    <button
                      onClick={() => openEditManutenzione(m)}
                      className="p-1.5 text-muted-color hover:text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors"
                      title="Modifica"
                    >
                      <Icon name="PencilIcon" size={15} />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm({ open: true, id: m.id, name: m.titolo, tipo: 'manutenzione' })}
                      className="p-1.5 text-muted-color hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Elimina"
                    >
                      <Icon name="TrashIcon" size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          ) : (
            changes.length === 0 ? <div className="text-center py-12 text-muted-color">Nessuna change request</div> :
            changes.map(c => (
              <div key={c.id} className="bg-surface-elevated border border-border rounded-xl p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-muted-color">{c.numero_change}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${changeStatoColor[c.stato] || ''}`}>{c.stato.replace(/_/g, ' ')}</span>
                      <span className="text-xs bg-surface text-muted-color px-2 py-0.5 rounded-full">{c.priorita}</span>
                    </div>
                    <p className="text-sm font-semibold text-white">{c.titolo}</p>
                    <p className="text-xs text-muted-color mt-1 line-clamp-2">{c.descrizione}</p>
                    <div className="flex flex-wrap gap-3 mt-1 text-xs text-muted-color">
                      {c.reparto && <span>📍 {c.reparto}</span>}
                      {c.proposto_da_nome && <span>👤 {c.proposto_da_nome}</span>}
                      {c.impatto && <span>⚠️ {c.impatto}</span>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {c.stato === 'in_approvazione' && (
                      <>
                        <button onClick={() => approvaChange(c.id, 'approvata')} className="bg-green-600/20 hover:bg-green-600/40 text-green-400 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors">Approva</button>
                        <button onClick={() => approvaChange(c.id, 'rifiutata')} className="bg-red-600/20 hover:bg-red-600/40 text-red-400 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors">Rifiuta</button>
                      </>
                    )}
                    {c.stato === 'approvata' && (
                      <button onClick={() => approvaChange(c.id, 'pianificata')} className="bg-purple-600/20 hover:bg-purple-600/40 text-purple-400 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors">Pianifica</button>
                    )}
                    {c.stato === 'pianificata' && (
                      <button onClick={() => approvaChange(c.id, 'in_esecuzione')} className="bg-orange-600/20 hover:bg-orange-600/40 text-orange-400 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors">Esegui</button>
                    )}
                    {c.stato === 'in_esecuzione' && (
                      <button onClick={() => approvaChange(c.id, 'verificata')} className="bg-teal-600/20 hover:bg-teal-600/40 text-teal-400 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors">Verifica</button>
                    )}
                    <button
                      onClick={() => setDeleteConfirm({ open: true, id: c.id, name: c.titolo, tipo: 'change' })}
                      className="p-1.5 text-muted-color hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
                      title="Elimina"
                    >
                      <Icon name="TrashIcon" size={15} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">{tab === 'manutenzioni' ? 'Nuova Manutenzione' : 'Nuova Change Request'}</h3>
              <button onClick={() => setShowForm(false)}><Icon name="XMarkIcon" size={20} className="text-muted-color" /></button>
            </div>
            {tab === 'manutenzioni' ? (
              <div className="space-y-3">
                <input value={formMan.titolo} onChange={e => setFormMan(p => ({ ...p, titolo: e.target.value }))} placeholder="Titolo *" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={formMan.tipo} onChange={e => setFormMan(p => ({ ...p, tipo: e.target.value }))} className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
                    <option value="preventiva">Preventiva</option>
                    <option value="correttiva">Correttiva</option>
                    <option value="straordinaria">Straordinaria</option>
                  </select>
                  <input value={formMan.reparto} onChange={e => setFormMan(p => ({ ...p, reparto: e.target.value }))} placeholder="Reparto" className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color" />
                </div>
                <input value={formMan.assegnato_a_nome} onChange={e => setFormMan(p => ({ ...p, assegnato_a_nome: e.target.value }))} placeholder="Tecnico assegnato" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color" />
                <input type="datetime-local" value={formMan.data_pianificata} onChange={e => setFormMan(p => ({ ...p, data_pianificata: e.target.value }))} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white" />
                <textarea value={formMan.note} onChange={e => setFormMan(p => ({ ...p, note: e.target.value }))} placeholder="Note..." rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color resize-none" />
                <div className="flex gap-3">
                  <button onClick={() => setShowForm(false)} className="flex-1 border border-border text-secondary-color py-2 rounded-lg text-sm font-semibold">Annulla</button>
                  <button onClick={creaManutenzione} disabled={saving} className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-semibold">{saving ? 'Salvataggio...' : 'Crea'}</button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <input value={formChange.titolo} onChange={e => setFormChange(p => ({ ...p, titolo: e.target.value }))} placeholder="Titolo *" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color" />
                <textarea value={formChange.descrizione} onChange={e => setFormChange(p => ({ ...p, descrizione: e.target.value }))} placeholder="Descrizione dettagliata..." rows={3} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color resize-none" />
                <div className="grid grid-cols-2 gap-3">
                  <select value={formChange.priorita} onChange={e => setFormChange(p => ({ ...p, priorita: e.target.value }))} className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white">
                    {['critico','urgente','alta','normale','bassa'].map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <input value={formChange.reparto} onChange={e => setFormChange(p => ({ ...p, reparto: e.target.value }))} placeholder="Reparto" className="bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color" />
                </div>
                <input value={formChange.proposto_da_nome} onChange={e => setFormChange(p => ({ ...p, proposto_da_nome: e.target.value }))} placeholder="Proposto da" className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color" />
                <textarea value={formChange.impatto} onChange={e => setFormChange(p => ({ ...p, impatto: e.target.value }))} placeholder="Impatto previsto..." rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color resize-none" />
                <textarea value={formChange.piano_rollback} onChange={e => setFormChange(p => ({ ...p, piano_rollback: e.target.value }))} placeholder="Piano di rollback..." rows={2} className="w-full bg-surface-elevated border border-border rounded-lg px-3 py-2 text-sm text-white placeholder-muted-color resize-none" />
                <div className="flex gap-3">
                  <button onClick={() => setShowForm(false)} className="flex-1 border border-border text-secondary-color py-2 rounded-lg text-sm font-semibold">Annulla</button>
                  <button onClick={creaChange} disabled={saving} className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white py-2 rounded-lg text-sm font-semibold">{saving ? 'Salvataggio...' : 'Crea'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
